import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import api from '../services/api';

const RewardsStorePage = ({ onVoltar }) => {
  const { user, token } = useUser();
  const [credito, setCredito] = useState(0);
  const [isApoiador, setIsApoiador] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [trocas, setTrocas] = useState([]);
  const [diasCronogramaExtra, setDiasCronogramaExtra] = useState(0);
  const [dataInicioDiasExtra, setDataInicioDiasExtra] = useState(null);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Obter o número de créditos do usuário
        const response = await api.get('/api/users/credito', config);
        setCredito(response.data.credito || 0);

        // Obter informações do cronograma extra
        const userResponse = await api.get('/api/users/me', config);
        setDiasCronogramaExtra(userResponse.data.user.diasCronogramaExtra || 0);
        setDataInicioDiasExtra(userResponse.data.user.dataInicioDiasExtra || null);

        // Verificar se o usuário é apoiador
        if (user) {
          setIsApoiador(user.isApoiador || user.isApoiaSeSupporter || false);
        }

        // Obter histórico de trocas
        const trocasResponse = await api.get('/api/users/trocas', config);
        setTrocas(trocasResponse.data.trocas || []);
      } catch (err) {
        setError('Erro ao carregar dados. Tente novamente mais tarde.');
        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDados();
    }
  }, [token, user]);

  const trocarCredito = async (dias, custo) => {
    if (isApoiador) {
      setError('Usuários apoiadores não podem trocar créditos por dias extras.');
      return;
    }

    if (credito < custo) {
      setError('Créditos insuficientes para esta troca.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await api.post('/api/users/trocar-credito', {
        dias,
        custo
      }, config);

      if (response.data.success) {
        setCredito(response.data.novoCredito);
        setSuccess(`Parabéns! Você ganhou ${dias} dias de acesso ao cronograma de estudos!`);

        // Atualizar informações do cronograma extra
        const userResponse = await api.get('/api/users/me', config);
        setDiasCronogramaExtra(userResponse.data.user.diasCronogramaExtra || 0);
        setDataInicioDiasExtra(userResponse.data.user.dataInicioDiasExtra || null);

        // Atualizar histórico de trocas
        const trocasResponse = await api.get('/api/users/trocas', config);
        setTrocas(trocasResponse.data.trocas || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao trocar créditos. Tente novamente.');
      console.error('Erro ao trocar créditos:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate remaining days
  const calcularDiasRestantes = () => {
    if (!dataInicioDiasExtra || diasCronogramaExtra <= 0) {
      return 0;
    }

    const startDate = new Date(dataInicioDiasExtra);
    const totalDays = diasCronogramaExtra;
    const now = new Date();

    // Calculate days since start date
    const timeDiff = now.getTime() - startDate.getTime();
    const daysSinceStart = Math.ceil(timeDiff / (1000 * 3600 * 24));

    // Calculate remaining days
    const remainingDays = Math.max(0, totalDays - daysSinceStart);

    return remainingDays;
  };

  const diasRestantes = calcularDiasRestantes();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando loja de recompensas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FC]">
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        <header className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Loja de Recompensas
          </h1>
          <button
            onClick={() => onVoltar('indique-e-ganhe')}
            className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
          >
            Voltar para Convidar
          </button>
        </header>

        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 text-green-700 p-4 rounded-lg mb-6">
            {success}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          {/* Seção de Créditos */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-800">Seus Créditos</h2>
                <p className="text-gray-600">Créditos disponíveis para troca</p>
              </div>
              <div className="mt-4 md:mt-0">
                <div className="bg-violet-100 text-violet-800 font-bold text-2xl px-6 py-3 rounded-lg">
                  {credito} crédito{credito !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            {isApoiador && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800 font-semibold">Você é um apoiador!</p>
                <p className="text-blue-700">Você já tem acesso ilimitado ao cronograma de estudos.</p>
              </div>
            )}
          </div>

          {/* Seção de Acesso ao Cronograma */}
          {!isApoiador && (diasCronogramaExtra > 0) && (
            <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Acesso ao Cronograma de Estudos</h2>
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                  <p className="text-gray-600">Dias extras adquiridos: <span className="font-semibold">{diasCronogramaExtra}</span></p>
                  <p className="text-gray-600">Dias restantes: <span className="font-semibold">{diasRestantes}</span></p>
                  {dataInicioDiasExtra && (
                    <p className="text-gray-600">Início do acesso: <span className="font-semibold">{new Date(dataInicioDiasExtra).toLocaleDateString('pt-BR')}</span></p>
                  )}
                </div>
                <div className={`mt-4 md:mt-0 px-6 py-3 rounded-lg font-bold text-center ${
                  diasRestantes > 10
                    ? 'bg-green-100 text-green-800'
                    : diasRestantes > 3
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                }`}>
                  {diasRestantes > 0
                    ? `${diasRestantes} dias restantes`
                    : 'Acesso expirado'}
                </div>
              </div>
            </div>
          )}

          {/* Seção de Opções de Troca */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">Troque seus créditos por dias grátis</h2>

            {isApoiador ? (
              <div className="text-center py-8">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 font-semibold">Você é um usuário apoiador!</p>
                  <p className="text-yellow-700 mt-2">Usuários apoiadores têm acesso ilimitado ao cronograma de estudos e não precisam trocar créditos por dias extras.</p>
                </div>
                <p className="text-gray-600">Continue compartilhando seu código de convite para ganhar reconhecimento por ajudar outros estudantes!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opção 1: 1 crédito por 7 dias */}
                <div className="border border-violet-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Pacote Básico</h3>
                      <p className="text-gray-600">7 dias grátis</p>
                    </div>
                    <div className="bg-violet-100 text-violet-800 font-bold px-3 py-1 rounded">
                      1 crédito
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">
                    Adicione 7 dias de acesso gratuito ao cronograma de estudos à sua conta.
                  </p>

                  <button
                    onClick={() => trocarCredito(7, 1)}
                    disabled={credito < 1}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      credito >= 1
                        ? 'bg-violet-600 text-white hover:bg-violet-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {credito >= 1 ? 'Trocar por 7 dias' : 'Créditos insuficientes'}
                  </button>
                </div>

                {/* Opção 2: 3 créditos por 30 dias */}
                <div className="border border-violet-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Pacote Premium</h3>
                      <p className="text-gray-600">30 dias grátis</p>
                    </div>
                    <div className="bg-violet-100 text-violet-800 font-bold px-3 py-1 rounded">
                      3 créditos
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">
                    Adicione 30 dias de acesso gratuito ao cronograma de estudos à sua conta.
                  </p>

                  <button
                    onClick={() => trocarCredito(30, 3)}
                    disabled={credito < 3}
                    className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                      credito >= 3
                        ? 'bg-violet-600 text-white hover:bg-violet-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {credito >= 3 ? 'Trocar por 30 dias' : 'Créditos insuficientes'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Seção de Histórico de Trocas */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Histórico de Trocas</h2>
            
            {trocas.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Você ainda não fez nenhuma troca.</p>
                <p className="text-gray-500 mt-2">Ganhe créditos indicando amigos!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-violet-100">
                      <th className="p-3 text-left text-violet-800 font-semibold">Data</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Descrição</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Dias</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Créditos Usados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trocas.map((troca, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 border-t border-gray-200">
                          {new Date(troca.data).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="p-3 border-t border-gray-200">{troca.descricao}</td>
                        <td className="p-3 border-t border-gray-200">{troca.dias} dias</td>
                        <td className="p-3 border-t border-gray-200">{troca.custo} crédito{troca.custo !== 1 ? 's' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardsStorePage;